import React, {Fragment, useContext, useEffect} from 'react';
import {ApiUtility} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ListPaginationOptions} from "../common/ListPaginationOptions";
import {GlobalContext, GlobalContextType} from "../admin-frame/AdminFrame";
import {CommonListPageHeader} from "../common/CommonListPageHeader";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {StringUtility} from "../common/StringUtility";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {UrlUtility} from "../common/UrlUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {SingleInvoice} from "./SingleInvoice";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {Constants} from "../common/Constants";
import {CreateSubscription} from "../subscriptions/CreateSubscription";
import {DateUtility} from "../common/DateUtility";
import {RouteComponentProps} from 'react-router-dom';
import {FpInvoice, Invoice} from "./InvoiceTypes";
import {Subscription} from "../subscriptions/SubscriptionTypes";

interface InvoiceListApiObject {
    invoice: Invoice;
    fpInvoice: FpInvoice;
    subscription_from_fp_invoice: Subscription;
}

interface InvoicesListPageURLQueryParams {
    page?: number;
}

interface InvoicesListPageRouteParams {
    invoiceId: string | undefined;
    fpInvoiceId: string | undefined;
    fpInvoiceIdForSubscription: string | undefined;
}

export const InvoicesListPage = function (
    props: RouteComponentProps<InvoicesListPageRouteParams>
) {
    const context: GlobalContextType = useContext(GlobalContext);
    const urlBase = '/invoices';
    const renderableData = useRenderableData({
        selectedPage: 1,
        showSingleInvoiceModal: false,
        showMakeSubscriptionModal: false,
        singleInvoiceId: undefined,
        singleFpInvoiceId: undefined,
        invoiceList: [],
        pageCount: 0,
        loading: false,

        prevUrlQueryString: undefined,
        prevParams: undefined,
    })

    // Update state if URL changes.
    if (
        (props.location && props.location.search) !== renderableData.prevUrlQueryString ||
        (props.match && props.match.params) !== renderableData.prevParams
    ) {
        let selectedPage = 1;
        let singleInvoiceId = null;
        let singleFpInvoiceId = null;
        let showSingleInvoiceModal = false;
        let showMakeSubscriptionModal = false;

        let routeParams = props.match && props.match.params;
        if (
            routeParams &&
            (routeParams['invoiceId'] || routeParams['fpInvoiceId'])
        ) {
            showSingleInvoiceModal = true;
            singleInvoiceId = routeParams['invoiceId'];
            singleFpInvoiceId = routeParams['fpInvoiceId'];
        }

        if (
            routeParams &&
            routeParams['fpInvoiceIdForSubscription']
        ) {
            singleFpInvoiceId = routeParams['fpInvoiceIdForSubscription'];
            showMakeSubscriptionModal = true;
        }

        let queryParams: InvoicesListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
        if (queryParams.page) {
            selectedPage = queryParams.page;
        }

        renderableData.updateAndRender({
            selectedPage: selectedPage,
            showSingleInvoiceModal: showSingleInvoiceModal || false,
            showMakeSubscriptionModal: showMakeSubscriptionModal || false,
            singleInvoiceId: singleInvoiceId,
            singleFpInvoiceId: singleFpInvoiceId,

            prevUrlQueryString: props.location && props.location.search,
            prevParams: props.match && props.match.params,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        [
            '' + renderableData.selectedPage
        ]
    );

    function reloadList(callback?: Function) {
        renderableData.updateAndRender({loading: true})
        ApiUtility.getInvoicesList(renderableData.selectedPage)
            .then(function (listData) {
                renderableData.updateAndRender({
                    ...listData,
                    loading: false
                });
                if (callback) {
                    callback();
                }
            })
            .catch(function (error) {
                renderableData.updateAndRender({
                    loading: false
                });
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the invoice list.`);
            });
    }

    function goToSingleInvoiceModal(invoiceId: number | undefined, fpInvoiceId: number | undefined) {
        if (invoiceId) {
            context.navigator.navigateTo(`${urlBase}/${invoiceId}${props.location.search}`);
        } else {
            context.navigator.navigateTo(`${urlBase}/fp-invoice/${fpInvoiceId}${props.location.search}`);
        }
    }

    function goToMakeSubscriptionModal(fpInvoiceId: number) {
        context.navigator.navigateTo(`${urlBase}/make-subscription-from-fp-invoice/${fpInvoiceId}${props.location.search}`);
    }

    function goToSubscription(subscriptionId: number) {
        context.navigator.navigateTo(`/subscriptions/${subscriptionId}`);
    }

    function closeModals() {
        context.navigator.navigateTo(`${urlBase}${props.location.search}`);
    }

    function afterSuccessfulSave(invoiceId: number) {
        closeModals()
        reloadList(
            () => goToSingleInvoiceModal(invoiceId, undefined)
        );
    }

    function afterSuccessfulCancel() {
        closeModals()
        reloadList()
    }

    function renderInvoicesListDisplay(invoicesList: InvoiceListApiObject[]) {
        let invoicesListDisplay = [];

        for (let i = 0; i < invoicesList.length; i++) {
            const invoiceObject = invoicesList[i];
            let fpInvoice = invoiceObject.fpInvoice;
            let invoice = invoiceObject.invoice;
            let description = fpInvoice.description || '';
            if (description.length > 200) {
                description = description.substring(0, 200) + '...';
            }
            let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status)
            let invoiceStatusDisplay = (
                <div className="invoice-status-content">
                    <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                        {invoice.invoice_status?.name}
                    </div>
                    <div className="invoice-status-additional-info">
                        {invoice.invoice_status?.additional_info}
                    </div>
                </div>
            )
            let invoiceType = Constants.invoiceTypes.fpOnly;
            let onClickInvoiceFunction =
                () => goToSingleInvoiceModal(invoiceObject.invoice.id, invoiceObject.fpInvoice.id)
            if (invoice.id) {
                invoiceType = Constants.invoiceTypes.oneOff
            } else if (invoiceObject.subscription_from_fp_invoice) {
                let subscriptionId = invoiceObject.subscription_from_fp_invoice.id
                invoiceType = Constants.invoiceTypes.subscription
                onClickInvoiceFunction = () => goToSubscription(subscriptionId)
                invoiceStatusDisplay = (
                    <div className="invoice-status-content">
                        <div
                            className="invoice-status-badge subscription"
                            title="Used to create a subscription"
                        >
                            SUBSCRIPTION
                        </div>
                    </div>
                )
            }

            let invoiceNumber = invoice.custom_invoice_number || fpInvoice.invoicenumber
            let fpInvoiceNumber = null;
            if (
                invoice.custom_invoice_number &&
                invoice.custom_invoice_number !== fpInvoice.invoicenumber
            ) {
                fpInvoiceNumber = (
                    <div className="fp-invoice-number">
                        {`(FP: ${fpInvoice.invoicenumber})`}
                    </div>
                )
            }

            let fpInvoiceDueDate = DateUtility.fpInvoiceDateToDisplayDate(fpInvoice.invoicedate)

            invoicesListDisplay.push(
                <div
                    key={i + 1}
                    id={`invoice-row-${fpInvoice.id}`}
                    className="common-list-row common-list-values-row"
                    onClick={onClickInvoiceFunction}
                >
                    <div className="common-list-row-content">
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-type"
                        >
                            <img
                                src={invoiceType.iconSrc}
                                title={invoiceType.name}
                                className={`invoice-number-icon ${invoiceType.className}`}
                            />
                        </div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-number"
                        >
                            <div className="invoice-number-cell-content">
                                <div className="billing-system-invoice-number">{invoiceNumber}</div>
                                {fpInvoiceNumber}
                            </div>
                        </div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell due-date"
                        >{fpInvoiceDueDate}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell company-name"
                        >{fpInvoice.hCompanyname}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-description"
                        >{description}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-total"
                        >{StringUtility.stringNumberToDisplayCurrency(invoice.billed_total || fpInvoice.grandusertotal)}</div>
                        <div
                            className="common-list-row-cell common-list-row-value-cell invoices-list-row-cell invoice-status"
                        >
                            {invoiceStatusDisplay}
                        </div>
                    </div>
                </div>
            );
        }

        return invoicesListDisplay;
    }

    if (renderableData.invoiceList) {
        return (
            <div className="common-list-page invoices-list-page">
                <CommonListPageHeader
                    headerText="Invoices"
                    rightHeaderRenderer={() => null}
                />
                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={renderableData.loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (renderableData.invoiceList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-header">
                                        <div className="common-list-row common-list-labels-row">
                                            <div className="common-list-row-content">
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-type"
                                                >Type
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-number"
                                                >Number
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell due-date"
                                                >Date (FP)
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell company-name"
                                                >Company
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-description"
                                                >Description
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-total"
                                                >Total
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell invoices-list-row-cell invoice-status"
                                                >Status
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-list-content">
                                        {renderInvoicesListDisplay(renderableData.invoiceList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={renderableData.pageCount}
                                        selectedPage={renderableData.selectedPage}
                                        urlBase="/invoices"
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No invoices found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
                {(() => {
                    if (renderableData.showSingleInvoiceModal) {
                        return (
                            <SingleInvoice
                                cancel={closeModals}
                                invoiceId={renderableData.singleInvoiceId}
                                fpInvoiceId={renderableData.singleFpInvoiceId}
                                afterSuccessfulSave={afterSuccessfulSave}
                                afterSuccessfulCancel={afterSuccessfulCancel}
                                onClickMakeSubscription={goToMakeSubscriptionModal}
                            />
                        );
                    } else if (renderableData.showMakeSubscriptionModal) {
                        return (
                            <CreateSubscription
                                cancel={closeModals}
                                afterSuccessfulSave={goToSubscription}
                                fromFpInvoiceId={renderableData.singleFpInvoiceId}
                            />
                        )
                    }
                })()}
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
